import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { API } from '@/App';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  Crown, Shield, Globe, Coins, TrendingUp, Database,
  RefreshCw, CheckCircle, Lock, ArrowRight, Layers
} from 'lucide-react';

const KALTransparency = () => {
  const [dashboard, setDashboard] = useState(null);
  const [blocks, setBlocks] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchData = async () => {
    try {
      const [dashRes, blocksRes] = await Promise.all([
        axios.get(`${API}/kal/dashboard`),
        axios.get(`${API}/kal/blockchain/blocks?count=5`)
      ]);
      setDashboard(dashRes.data);
      setBlocks(blocksRes.data.blocks || []);
    } catch (err) {
      console.error('Error:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 60000);
    return () => clearInterval(interval);
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin h-12 w-12 border-4 border-amber-500 border-t-transparent rounded-full"></div>
      </div>
    );
  }

  const d = dashboard;
  const reserves = d?.reserves || {};
  const policy = d?.monetary_policy || {};
  const chain = d?.blockchain || {};
  const stats = d?.network_stats || {};
  const backing = d?.asset_backing || {};

  return (
    <div className="min-h-screen bg-background" data-testid="tnb-transparency-page">
      {/* Header */}
      <header className="sticky top-0 z-50 glass border-b border-border/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-3" data-testid="nav-logo">
              <div className="w-9 h-9 gold-gradient rounded-sm flex items-center justify-center">
                <Crown className="h-5 w-5 text-black" />
              </div>
              <span className="font-serif text-lg font-semibold hidden sm:block">TANUB</span>
            </Link>
            <div className="hidden md:flex items-center gap-6">
              <Link to="/" className="text-sm text-muted-foreground hover:text-foreground">Home</Link>
              <Link to="/tnb/market" className="text-sm text-muted-foreground hover:text-foreground">Market</Link>
              <Link to="/public" className="text-sm text-muted-foreground hover:text-foreground">CBDC Portal</Link>
            </div>
            <div className="flex items-center gap-3">
              <Link to="/tnb/market">
                <Button className="btn-royal-gold" size="sm" data-testid="trade-btn">Trade TNB</Button>
              </Link>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Hero Banner */}
        <div className="mb-8 p-8 rounded-xl bg-black text-white">
          <div className="flex items-center gap-2 mb-3">
            <Shield className="h-5 w-5 text-gold" />
            <span className="font-mono text-xs uppercase tracking-widest text-gold">Public Transparency Dashboard</span>
          </div>
          <h1 className="font-serif text-3xl md:text-4xl font-light mb-2" data-testid="transparency-title">
            TANUB Reserve & Policy Status
          </h1>
          <p className="text-white/60 max-w-2xl">
            Full transparency into TNB reserves, monetary policy, blockchain ledger, and network health. All data is publicly auditable.
          </p>
        </div>

        {/* Key Metrics */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          {[
            { label: 'KAL / USD', value: `$${d?.rates?.kal_usd?.toFixed(2) || '0'}`, icon: <Coins className="h-4 w-4" /> },
            { label: 'Reserve Ratio', value: `${(reserves.backing_ratio * 100).toFixed(0) || 115}%`, icon: <Shield className="h-4 w-4" /> },
            { label: 'Supply', value: `${(policy.current_supply / 1e9).toFixed(1)}B`, icon: <TrendingUp className="h-4 w-4" /> },
            { label: 'Active Wallets', value: `${(stats.total_wallets / 1e6).toFixed(1)}M`, icon: <Globe className="h-4 w-4" /> },
          ].map((m, i) => (
            <Card key={i} className="dashboard-card" data-testid={`metric-${i}`}>
              <CardContent className="p-4">
                <div className="flex items-center gap-2 text-muted-foreground mb-1">{m.icon}<span className="text-xs uppercase">{m.label}</span></div>
                <p className="font-mono text-2xl font-bold">{m.value}</p>
              </CardContent>
            </Card>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Reserve Breakdown */}
          <Card className="sovereign-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Shield className="h-5 w-5 text-gold" /> Reserve Backing
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex justify-between items-center p-3 rounded-lg bg-yellow-50 border border-yellow-200">
                  <span className="font-medium">Gold Reserves</span>
                  <div className="text-right">
                    <p className="font-mono font-bold">{(reserves.gold_reserves_oz || 0).toLocaleString()} oz</p>
                    <p className="text-xs text-muted-foreground">${((reserves.gold_value_usd || 0) / 1e9).toFixed(1)}B</p>
                  </div>
                </div>
                <div className="flex justify-between items-center p-3 rounded-lg bg-blue-50 border border-blue-200">
                  <span className="font-medium">Fiat Reserves</span>
                  <div className="text-right">
                    {Object.entries(reserves.fiat_reserves || {}).slice(0, 3).map(([k, v]) => (
                      <p key={k} className="text-xs font-mono">{k}: ${(v / 1e9).toFixed(1)}B</p>
                    ))}
                  </div>
                </div>
                <div className="flex justify-between items-center p-3 rounded-lg bg-purple-50 border border-purple-200">
                  <span className="font-medium">Crypto Reserves</span>
                  <div className="text-right">
                    {Object.entries(reserves.crypto_reserves || {}).slice(0, 3).map(([k, v]) => (
                      <p key={k} className="text-xs font-mono">{k}: {typeof v === 'number' && v > 1e6 ? `$${(v / 1e9).toFixed(1)}B` : v.toLocaleString()}</p>
                    ))}
                  </div>
                </div>
                <div className="flex justify-between items-center p-3 rounded-lg bg-emerald-50 border border-emerald-200">
                  <span className="font-medium">Real Assets</span>
                  <div className="text-right">
                    <p className="text-xs font-mono">Real Estate: ${((reserves.real_estate_value_usd || 0) / 1e9).toFixed(1)}B</p>
                    <p className="text-xs font-mono">Infrastructure: ${((reserves.infrastructure_value_usd || 0) / 1e9).toFixed(1)}B</p>
                    <p className="text-xs font-mono">Resources: ${((reserves.natural_resources_value_usd || 0) / 1e9).toFixed(1)}B</p>
                  </div>
                </div>
                <div className="p-4 rounded-lg bg-black text-white">
                  <div className="flex justify-between">
                    <span className="text-white/70">Total Reserves</span>
                    <span className="font-mono text-xl font-bold text-gold">${((reserves.total_reserves_usd || 0) / 1e9).toFixed(1)}B</span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Monetary Policy */}
          <Card className="sovereign-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <TrendingUp className="h-5 w-5 text-gold" /> Monetary Policy
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {[
                  { label: 'Target Inflation', value: `${policy.target_inflation}%` },
                  { label: 'Actual Inflation', value: `${policy.actual_inflation}%`, highlight: policy.actual_inflation <= policy.target_inflation },
                  { label: 'Base Interest Rate', value: `${policy.base_interest_rate}%` },
                  { label: 'Reserve Requirement', value: `${policy.reserve_requirement}%` },
                  { label: 'Circulating Supply', value: `${(policy.current_supply / 1e9).toFixed(1)}B KAL` },
                  { label: 'Max Supply', value: `${(policy.max_supply / 1e9).toFixed(0)}B KAL` },
                ].map((item, i) => (
                  <div key={i} className="flex justify-between items-center py-2 border-b border-border/50 last:border-0">
                    <span className="text-muted-foreground text-sm">{item.label}</span>
                    <span className={`font-mono font-medium ${item.highlight ? 'text-emerald-600' : ''}`}>{item.value}</span>
                  </div>
                ))}
              </div>
              <div className="mt-4 p-3 rounded-lg bg-blue-50 border border-blue-200">
                <p className="text-xs text-blue-600 font-medium mb-1">AI Policy Recommendation</p>
                <p className="text-sm text-blue-800">{policy.ai_recommendation}</p>
              </div>
              <div className="mt-3">
                <p className="text-xs text-muted-foreground uppercase tracking-wider mb-2">Peg Deviation</p>
                <div className="grid grid-cols-4 gap-2">
                  {Object.entries(policy.peg_deviation || {}).map(([k, v]) => (
                    <div key={k} className="text-center p-2 rounded bg-muted/50">
                      <p className="text-xs font-medium">{k}</p>
                      <p className={`font-mono text-sm ${Math.abs(v) <= 0.05 ? 'text-emerald-600' : 'text-amber-600'}`}>
                        {v > 0 ? '+' : ''}{(v * 100).toFixed(1)}%
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Blockchain Ledger */}
          <Card className="sovereign-card lg:col-span-2">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <Database className="h-5 w-5 text-gold" /> Blockchain Ledger
                </CardTitle>
                <div className="flex items-center gap-2">
                  <CheckCircle className={`h-4 w-4 ${chain.is_valid ? 'text-emerald-500' : 'text-red-500'}`} />
                  <span className="text-xs font-mono">{chain.is_valid ? 'Chain Valid' : 'Chain Invalid'}</span>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                {[
                  { label: 'Blocks', value: chain.chain_length },
                  { label: 'Transactions', value: chain.total_transactions },
                  { label: 'Pending', value: chain.pending_transactions },
                  { label: 'Difficulty', value: chain.difficulty },
                ].map((s, i) => (
                  <div key={i} className="text-center p-3 rounded-lg bg-muted/50">
                    <p className="text-xs text-muted-foreground uppercase">{s.label}</p>
                    <p className="font-mono text-xl font-bold">{s.value}</p>
                  </div>
                ))}
              </div>
              <p className="text-xs text-muted-foreground uppercase tracking-wider mb-3">Recent Blocks</p>
              <div className="space-y-2">
                {blocks.map((block, i) => (
                  <div key={i} className="p-3 rounded-lg bg-muted/30 border border-border/50" data-testid={`block-${i}`}>
                    <div className="flex items-center justify-between mb-1">
                      <div className="flex items-center gap-2">
                        <Layers className="h-4 w-4 text-gold" />
                        <span className="font-mono text-sm font-bold">Block #{block.index}</span>
                      </div>
                      <span className="text-xs text-muted-foreground">{block.transaction_count} txn(s)</span>
                    </div>
                    <p className="font-mono text-xs text-muted-foreground truncate">Hash: {block.hash}</p>
                    <p className="font-mono text-xs text-muted-foreground truncate">Prev: {block.previous_hash}</p>
                    <p className="text-xs text-muted-foreground mt-1">{block.datetime && new Date(block.datetime).toLocaleString()}</p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* CTA */}
        <div className="mt-8 p-8 rounded-xl gold-gradient text-center">
          <h2 className="font-serif text-2xl md:text-3xl font-light text-black mb-3">Start Trading TANUB</h2>
          <p className="text-black/70 mb-6">Buy, sell, and convert TNB with real-time gold-pegged rates</p>
          <Link to="/tnb/market">
            <Button size="lg" className="bg-black hover:bg-black/90 text-white px-8" data-testid="trade-cta">
              Go to Market <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </Link>
        </div>
      </main>
    </div>
  );
};

export default KALTransparency;
